<template>
  <div class="timetable-item">
    <div class="timetable-item__main">
      <time v-if="raceDataItem.startDate" class="timetable-item__time">
        {{ getStartTime(raceDataItem.startDate) }}<span>-</span>
      </time>
      <p class="timetable-item__title">{{ raceDataItem.raceTitle }}</p>
    </div>
    <div class="timetable-item__tool">
      <atom-icon-router-button
        class="timetable-item__btn"
        :class="{
          'timetable-item__btn--disabled': isDisabled,
          'timetable-item__btn--circuit-mode': enableCircuitMode,
        }"
        :to="!isDisabled ? linkTo(raceDataItem) : ''"
        :linkText="''"
        theme="light"
        colorType="secondary"
        size="xsmall"
        :imgPath="setPlayIcon()"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import dayjs from 'dayjs'
import StoreUtil from '@/store/StoreUtil'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'
import ContentsInfoDocument from '@/store/stores/collectionModule/documents/contents/ContentsInfoDocument'
import AngleMovieInfoDocument from '@/store/stores/collectionModule/documents/angleMovie/AngleMovieInfoDocument'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import useMovieContentsStatus from '@/components/hook/useMovieContentsStatus'
import AtomIconRouterButton from '@/components/atoms/AtomIconRouterButton.vue'

/* eslint-disable @typescript-eslint/no-var-requires */
const playIconPath = require('@/assets/img/icon/icon_play__green.svg')
/* eslint-disable @typescript-eslint/no-var-requires */
const disabledPlayIconPath = require('@/assets/img/icon/icon_play__disabled.svg')
/* eslint-disable @typescript-eslint/no-var-requires */
const circuitPlayIconPath = require('@/assets/img/icon/icon_play__white.svg')

export default defineComponent({
  name: 'TimetableItemParts',
  components: { AtomIconRouterButton },
  props: {
    /**
     * レース情報
     */
    raceDataItem: {
      type: Object as PropType<RaceDocument>,
      required: true,
    },
    /**
     * コンテンツ動画情報
     */
    contentsInfos: {
      type: Array as PropType<Array<ContentsInfoDocument>>,
      required: true,
    },
    /**
     * アングル動画情報
     */
    angleMovieInfos: {
      type: Array as PropType<Array<AngleMovieInfoDocument>>,
      required: true,
    },
    /**
     * 予選レースに登録されているスタートイベント
     */
    qualifyingRaceStartEventList: {
      type: Array as PropType<Array<HighlightDocument>>,
    },
  },
  setup(props) {
    const appConfigStore = StoreUtil.useStore('AppConfigStore')
    const { hasDistributedMovie } = useMovieContentsStatus()

    /**
     * ボタンを押せない状態かどうか
     */
    const isDisabled = computed(
      () =>
        !hasDistributedMovie(
          props.raceDataItem,
          props.contentsInfos,
          props.angleMovieInfos,
          props.qualifyingRaceStartEventList,
        ),
    )

    const enableCircuitMode = computed(() => {
      // ボタンが disabled だったらボタンの色を変更しない
      if (isDisabled.value) {
        return false
      }

      // サーキットモードになったら再生ボタンの色を変更する
      return appConfigStore.currentCircuitMode.value
    })

    /**
     * レースの開始時間を返す
     */
    const getStartTime = (startDate: number) => {
      if (!startDate) return ''
      return dayjs(startDate).tz('Asia/Tokyo').format('HH:mm')
    }

    return {
      enableCircuitMode,
      hasDistributedMovie,
      isDisabled,
      getStartTime,
    }
  },
  methods: {
    /**
     * 指定されたレースの動画再生画面に遷移する。
     * @param race レース
     */
    linkTo(race: RaceDocument) {
      if (race.highlightPlayInfo?.highlightId) {
        return `/race-video/${race.championshipMasterId}/${race.id}/${race.highlightPlayInfo.highlightId}/`
      }
      return `/race-video/${race.championshipMasterId}/${race.id}/`
    },
    /**
     * 動画再生画面アイコンの出し分け
     */
    setPlayIcon(): string {
      if (this.isDisabled) {
        return disabledPlayIconPath
      }
      return this.enableCircuitMode ? circuitPlayIconPath : playIconPath
    },
  },
})
</script>

<style scoped lang="scss">
.timetable-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-top: 1px solid $black10;

  &__time {
    font-size: 10px;
    font-weight: bold;
    color: $black50;
    letter-spacing: 0.04em;

    span {
      margin-left: 0.25em;
    }
  }

  &__title {
    padding-right: 8px;
    font-size: 14px;
    font-weight: 700;
    color: $black;
    letter-spacing: 0.02em;
    word-break: break-all;
  }

  &__tool {
    flex-shrink: 0;
    width: 58px;
  }

  &__link {
    @include button();

    &--small {
      color: #00dba6;
    }

    &--light--secondary {
      &[disabled] {
        color: #c7c7c7;
        background-color: #e5e5e5;
        border-color: rgba(#000, 0.05);
      }
    }

    &--circuit-mode {
      color: #fff;
    }
  }

  &__btn {
    ::v-deep {
      .atom-icon-router-btn--has-icon {
        padding: 0 1.133em;

        img {
          margin-right: 0;
        }
      }
    }

    &--disabled {
      ::v-deep {
        .atom-icon-router-btn--has-icon {
          background-color: #e5e5e5;
          border-color: rgba(#000, 0.05);
        }
      }
    }

    &--circuit-mode {
      ::v-deep {
        .atom-icon-router-btn--has-icon {
          border-color: rgba(0, 0, 0, 0.2);
          background: linear-gradient(
            90deg,
            rgba(125, 46, 163, 0.85) 0%,
            rgba(202, 140, 232, 0.85) 72.52%,
            rgba(125, 46, 163, 0.85) 107.1%
          );
        }
      }
    }
  }
}
</style>
